import { useQuery } from "@tanstack/react-query";

import {
  ISurveyLanguage,
  ISurveyListItem,
  ISurveyTranslation,
} from "../../../@types/admin/AdminSurveyTranslation";
import { surveyTranslationsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

export function surveyTranslationsKey(surveyId: string | undefined) {
  return ["admin-survey-translations", surveyId];
}

/** Surveys available to translate. Catalogue-sized and stable. */
export function useTranslatableSurveys() {
  const { data, isLoading, isError } = useQuery<ISurveyListItem[]>({
    queryKey: ["admin-translatable-surveys"],
    queryFn: async () => ensureOk(await surveyTranslationsService.getSurveys()),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { surveys: data ?? [], isLoading, isError };
}

/** One survey's translations. Idle until a survey is chosen. */
export function useSurveyTranslations(surveyId: string | undefined) {
  const { data, isLoading, isError, isFetching } = useQuery<
    ISurveyTranslation[]
  >({
    queryKey: surveyTranslationsKey(surveyId),
    queryFn: async () =>
      ensureOk(
        await surveyTranslationsService.getSurveyTranslations(surveyId!)
      ),
    enabled: Boolean(surveyId),
    refetchOnMount: "always",
  });

  return { translations: data ?? [], isLoading, isFetching, isError };
}

/** Language catalogue for the add-language action. */
export function useSurveyLanguages() {
  const { data, isLoading, isError } = useQuery<ISurveyLanguage[]>({
    queryKey: ["admin-survey-languages"],
    queryFn: async () =>
      ensureOk(await surveyTranslationsService.getLanguages()),
  });

  return { languages: data ?? [], isLoading, isError };
}
